"use client";

import Link from "next/link";
import { Sparkles, PlusCircle, User, LayoutDashboard, LogOut, X, Mail, Lock, ArrowRight } from "lucide-react";
import { useState, useRef, useEffect } from "react";
import CartHeaderButton from "@/components/store/CartHeaderButton";

interface SoftNavbarProps {
  subdomain: string;
  storeName?: string;
}

export default function SoftNavbar({ subdomain, storeName }: SoftNavbarProps) {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [isAuthOpen, setIsAuthOpen] = useState(false);
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");

  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setDropdownOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleAuthSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const endpoint = isLogin 
        ? `http://${subdomain}.localhost:8000/api/v1/customers/login/` 
        : `http://${subdomain}.localhost:8000/api/v1/customers/register/`;

      const res = await fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password, name }),
      });

      const data = await res.json();

      if (res.ok) {
        alert(isLogin ? "Login Successful!" : "Registration Successful!");
        setIsAuthOpen(false);
        window.location.reload();
      } else {
        alert(data.error || data.detail || "Something went wrong!");
      }
    } catch (err) {
      console.error("Auth error:", err);
      alert("Server connection failed.");
    }
  };

  return (
    <>
      <header className="sticky top-0 z-50 bg-white/90 backdrop-blur-md border-b border-slate-100 shadow-[0_4px_20px_rgba(0,0,0,0.04)] font-sans">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-20 flex items-center justify-between gap-4">
          
          <Link href="/" className="flex items-center gap-2.5">
            <div className="w-10 h-10 rounded-2xl bg-gradient-to-tr from-blue-600 to-sky-400 flex items-center justify-center text-white shadow-md shadow-blue-500/20 font-black text-xl">
              <Sparkles className="w-5 h-5" />
            </div>
            <span className="font-black text-2xl tracking-tight text-slate-900 capitalize">
              {storeName || subdomain}
              <span className="text-blue-600">.</span>
            </span>
          </Link>

          <nav className="hidden md:flex items-center gap-6 text-xs font-bold text-slate-600">
            <a href="#categories" className="hover:text-blue-600 transition-colors">Categories</a>
            <a href="#featured" className="hover:text-blue-600 transition-colors">Featured</a>
            <a href="#deals" className="hover:text-blue-600 transition-colors">Fast Deals</a>
            <a href="#faq" className="hover:text-blue-600 transition-colors">Help & FAQs</a>
          </nav>

          <div className="flex items-center gap-3">
            <CartHeaderButton />

            <button
              type="button"
              onClick={() => setIsAuthOpen(true)}
              className="px-4 py-2.5 rounded-2xl bg-white hover:bg-slate-50 border border-slate-200/80 text-xs font-black text-slate-800 shadow-[4px_4px_10px_#dce5ef,-4px_-4px_10px_#ffffff] transition-all cursor-pointer"
            >
              Login / Register
            </button>

            <div className="relative" ref={dropdownRef}>
              <button
                type="button"
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className="flex items-center gap-2 px-3.5 py-2 rounded-2xl bg-slate-50 hover:bg-slate-100 border border-slate-200/80 text-xs font-black text-slate-800 transition-all cursor-pointer shadow-sm"
              >
                <div className="w-7 h-7 rounded-xl bg-blue-600 text-white flex items-center justify-center text-xs font-black">
                  <User className="w-3.5 h-3.5" />
                </div>
                <span className="hidden sm:inline">Dashboard</span>
              </button>

              {dropdownOpen && (
                <div className="absolute right-0 mt-3 w-52 bg-white rounded-3xl p-3 shadow-2xl border border-slate-100 space-y-1.5 animate-in fade-in zoom-in-95">
                  <Link
                    href="/dashboard"
                    onClick={() => setDropdownOpen(false)}
                    className="flex items-center gap-2.5 px-4 py-2.5 rounded-2xl text-xs font-bold text-slate-700 hover:bg-blue-50 hover:text-blue-600 transition-colors"
                  >
                    <LayoutDashboard className="w-4 h-4 text-blue-600" />
                    <span>Soft Dashboard</span>
                  </Link>

                  <button
                    type="button"
                    onClick={() => {
                      setDropdownOpen(false);
                      alert("Logged out successfully");
                    }}
                    className="w-full flex items-center gap-2.5 px-4 py-2.5 rounded-2xl text-xs font-bold text-rose-600 hover:bg-rose-50 transition-colors text-left cursor-pointer"
                  >
                    <LogOut className="w-4 h-4" />
                    <span>Logout</span>
                  </button>
                </div>
              )}
            </div>

            <button
              type="button"
              onClick={() => alert("Post Ad / Listing feature is active!")}
              className="hidden sm:flex items-center gap-2 px-5 py-2.5 rounded-full bg-blue-600 hover:bg-blue-500 text-white text-xs font-black shadow-lg shadow-blue-500/25 transition-all cursor-pointer"
            >
              <PlusCircle className="w-4 h-4" />
              <span>Start Ad</span>
            </button>
          </div>
        </div>
      </header>

      {isAuthOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 backdrop-blur-sm p-4">
          <div className="bg-[#F0F4F9] w-full max-w-md rounded-[36px] p-8 shadow-[16px_16px_30px_#dce5ef,-16px_-16px_30px_#ffffff] border border-white relative space-y-6">
            
            <button
              onClick={() => setIsAuthOpen(false)}
              className="absolute top-6 right-6 w-9 h-9 rounded-2xl bg-white shadow-[4px_4px_10px_#dce5ef,-4px_-4px_10px_#ffffff] flex items-center justify-center text-slate-500 hover:text-slate-800 transition-colors cursor-pointer"
            >
              <X className="w-4 h-4" />
            </button>

            <div className="text-center space-y-1 pt-2">
              <span className="text-[10px] font-black uppercase tracking-wider text-blue-600">
                {subdomain} Marketplace
              </span>
              <h2 className="text-2xl font-black text-slate-900">
                {isLogin ? "Welcome Back!" : "Create Account"}
              </h2>
              <p className="text-xs text-slate-400">
                {isLogin ? "Login to access your dashboard & listings" : "Sign up to post ads and manage orders"}
              </p>
            </div>

            <div className="flex bg-white p-1.5 rounded-2xl shadow-inner border border-slate-100">
              <button
                type="button"
                onClick={() => setIsLogin(true)}
                className={`flex-1 py-2.5 rounded-xl text-xs font-bold transition-all cursor-pointer ${
                  isLogin ? "bg-blue-600 text-white shadow-md shadow-blue-500/20" : "text-slate-500 hover:text-slate-900"
                }`}
              >
                Login
              </button>
              <button
                type="button"
                onClick={() => setIsLogin(false)}
                className={`flex-1 py-2.5 rounded-xl text-xs font-bold transition-all cursor-pointer ${
                  !isLogin ? "bg-blue-600 text-white shadow-md shadow-blue-500/20" : "text-slate-500 hover:text-slate-900"
                }`}
              >
                Register
              </button>
            </div>

            <form onSubmit={handleAuthSubmit} className="space-y-4 text-xs">
              {!isLogin && (
                <div className="space-y-1">
                  <label className="font-bold text-slate-700 ml-1">Full Name</label>
                  <div className="relative flex items-center">
                    <User className="w-4 h-4 absolute left-4 text-slate-400" />
                    <input
                      type="text"
                      required
                      placeholder="Ritu Sk"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      className="w-full pl-11 pr-4 py-3 rounded-2xl bg-white border border-white shadow-[inset_3px_3px_6px_#dce5ef,inset_-3px_-3px_6px_#ffffff] text-slate-800 outline-none"
                    />
                  </div>
                </div>
              )}

              <div className="space-y-1">
                <label className="font-bold text-slate-700 ml-1">Email Address</label>
                <div className="relative flex items-center">
                  <Mail className="w-4 h-4 absolute left-4 text-slate-400" />
                  <input
                    type="email"
                    required
                    placeholder="user@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full pl-11 pr-4 py-3 rounded-2xl bg-white border border-white shadow-[inset_3px_3px_6px_#dce5ef,inset_-3px_-3px_6px_#ffffff] text-slate-800 outline-none"
                  />
                </div>
              </div>

              <div className="space-y-1">
                <label className="font-bold text-slate-700 ml-1">Password</label>
                <div className="relative flex items-center">
                  <Lock className="w-4 h-4 absolute left-4 text-slate-400" />
                  <input
                    type="password"
                    required
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full pl-11 pr-4 py-3 rounded-2xl bg-white border border-white shadow-[inset_3px_3px_6px_#dce5ef,inset_-3px_-3px_6px_#ffffff] text-slate-800 outline-none"
                  />
                </div>
              </div>

              <button
                type="submit"
                className="w-full mt-2 py-3.5 rounded-full bg-blue-600 hover:bg-blue-500 text-white font-black tracking-wide shadow-lg shadow-blue-500/30 transition-all flex items-center justify-center gap-2 cursor-pointer"
              >
                <span>{isLogin ? "Login to Dashboard" : "Create Account"}</span>
                <ArrowRight className="w-4 h-4" />
              </button>
            </form>

          </div>
        </div>
      )}
    </>
  );
}